import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CheckOutlined, SettingOutlined, WarningOutlined } from '@ant-design/icons';
import GlobalBarcodeInputImpl from '@components/operator/GlobalBarcodeInput';
import VideoUploadQueueStatus from '@components/operator/qc/VideoUploadQueueStatus';
import VideoRecorder from '@components/operator/return_verification/VideoRecorder';
import type { ReturnVerificationVideoRecorderHandle } from '@components/operator/return_verification/VideoRecorder';
import { BarcodeInputProvider, useBarcodeInput, useBarcodeInputContext } from '@contexts/operator/BarcodeInputContext';
import ConfirmVerificationModal from '@components/operator/return_verification/ConfirmVerificationModal';
import CreateReturnOrderModal from '@components/operator/return_verification/CreateReturnOrderModal';
import ResultSelectModal from '@components/operator/return_verification/ResultSelectModal';
import ReturnReceiptItemsTable from '@components/operator/return_verification/ReturnReceiptItemsTable';
import ReturnReceiptSelectorModal from '@components/operator/return_verification/ReturnReceiptSelectorModal';
import ReturnVerificationDrawer from '@components/operator/return_verification/ReturnVerificationDrawer';
import { ReturnReceiptAcceptanceProvider, useReturnReceiptAcceptance, productMatchesCode } from '@contexts/operator/ReturnReceiptAcceptanceContext';
import { useReturnReceiptChunkedUploadQueue } from '@hooks/operator/useReturnReceiptChunkedUploadQueue';
import { useWhiteRegionDetector } from '@hooks/operator/useWhiteRegionDetector';
import soundEffects from '@utils/soundEffects';
import { Alert, Empty, Spin, Button, Collapse, Switch, Modal, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';

import { getSpecialBarcode } from '@constants/specialBarcodes';

import returnReceiptService from '@services/operator/returnReceiptService';

const __DEBUG__ = false;

// 影片字幕（掃描商品時短暫蓋在錄影預覽上的提示）
interface Subtitle {
  text: string;
  timestamp: number;
  color?: string;
  bgColor?: string;
}

// useWhiteRegionDetector 偵測到的單據區域（畫面相對座標，0~1）
interface DetectedRegion {
  x: number;
  y: number;
  w: number;
  h: number;
}

// 推算出的放大區域 — 給 <VideoRecorder magnifier={...} /> 用
interface Magnifier {
  x: number;
  y: number;
  zoom: number;
  sizeW: number;
  sizeH: number;
}

// 後端 /return_receipts/search 結果的最小欄位（給 Result/Selector modal 用）
// 維持寬鬆—實際欄位由 BE 控制，這裡只標住我們會讀的。
interface SearchResult {
  match_count?: number;
  match_type?: string;
  results?: SearchResult[];
  fulfillment?: { id: string; name?: string; number?: string } | null;
  return_order?: { id: string; name?: string } | null;
  existing_return_order?: { id: string } | null;
  merchant?: { id: string } | null;
  active_pending_receipts?: unknown[];
  [key: string]: unknown;
}

// 驗收頁的品項列（混了 planned 與 unplanned）— 受 ReturnReceiptAcceptanceContext 控制
interface VerificationItem {
  id: string;
  product_id?: string;
  inventory_type_id?: string | null;
  quantity?: number;
  received_quantity?: number;
  batch?: string | null;
  expiration_date?: string | null;
  return_order_item_id?: string | null;
  product?: { id: string; sku: string; name: string; barcode?: string } | null;
  unplanned?: boolean;
  mismatch?: boolean;
  [key: string]: unknown;
}

function ReturnOrderVerificationContent() {
  const { t } = useTranslation();
  // .jsx Contexts 用 `createContext(null)` / `createContext()`，TS 把 useContext
  // 的回傳推成 null/undefined，destructure 出來會變 never。以 `as any` 卸掉這
  // 個型別誤推；context 內部結構由 ReturnReceiptAcceptanceContext.jsx 控制，
  // 等該檔案轉 ts 時再回頭補真實型別（這次 PR 只轉 page）。
  const { clear: clearBarcodeInput, focus: focusBarcodeInput } = useBarcodeInputContext() as any;

  // Context
  const {
    loading: contextLoading, error: contextError, setError: setContextError,
    searchResult, items, scannedItem, setScannedItem,
    inventoryTypes, merchantId, defaultReturnInventoryTypeId,
    anomalyMode, setAnomalyMode,
    search, loadResult, loadResultWithReceipt, scanProduct, verifyItem, reset,
  } = useReturnReceiptAcceptance() as any;

  // GlobalBarcodeInput 還是 .jsx：`className = ''` 預設讓 TS 誤把 className 當 required prop。
  // Body-level cast 避免 module top-level TDZ（Vite HMR）。
  const GlobalBarcodeInput = GlobalBarcodeInputImpl as React.ComponentType<any>;

  // 影片錄製（chunked 上傳到 GCS，worker 合併；同 QC 流程）
  const [subtitle, setSubtitle] = useState<Subtitle | null>(null);
  const uploadQueue = useReturnReceiptChunkedUploadQueue();
  const videoRecorderRef = useRef<ReturnVerificationVideoRecorderHandle | null>(null);
  const prevSearchResultRef = useRef<SearchResult | null>(null);
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);

  // 自動偵測托運單 → 放大（可開關）
  const [autoZoomEnabled, setAutoZoomEnabled] = useState(true);
  // useWhiteRegionDetector.js 從內部 `videoEl.videoWidth` 等用法被 TS 推成
  // 接 HTMLVideoElement（非可空），我們的 state 是 nullable，所以 as any 過。
  const { region: detectedRegion, debug: detectorDebug } = useWhiteRegionDetector(videoEl as any, {
    enabled: autoZoomEnabled && !!searchResult,
    interval: 600,
  } as any) as { region: DetectedRegion | null; debug: any };
  const magnifier: Magnifier | null = (() => {
    if (!detectedRegion) return null;
    // 偵測區域寬和高都超過 65% → 單據已佔滿畫面，不需放大
    // 偵測區域面積超過畫面 20% → 單據已夠近，不需放大
    const regionArea = detectedRegion.w * detectedRegion.h;
    if (regionArea >= 0.2) return null;
    const MAX_ZOOM = 5;
    const sizeW = Math.max(0.45, detectedRegion.w * 2);
    const sizeH = Math.max(0.35, detectedRegion.h * 2);
    const zoomW = sizeW / detectedRegion.w;
    const zoomH = sizeH / detectedRegion.h;
    const zoom = Math.min(zoomW, zoomH, MAX_ZOOM);
    return { x: detectedRegion.x, y: detectedRegion.y, zoom, sizeW, sizeH };
  })();

  // Local state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightedItemId, setHighlightedItemId] = useState<string | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  // ResultSelectModal local state
  const [resultSelectModalOpen, setResultSelectModalOpen] = useState(false);
  const [pendingResults, setPendingResults] = useState<SearchResult[]>([]);
  const [pendingMatchType, setPendingMatchType] = useState<string | null>(null);

  // ReturnReceiptSelectorModal local state — pick which pending receipt to resume
  const [receiptSelectorOpen, setReceiptSelectorOpen] = useState(false);
  const [pendingResultForReceipt, setPendingResultForReceipt] = useState<SearchResult | null>(null);

  // CreateReturnOrderModal local state — fulfillment scanned but no existing RO yet.
  // Operator confirms (or edits) the suggested RO name; on confirm we splice
  // the new RO into the search result and continue down the unified RO path.
  const [createRoModalOpen, setCreateRoModalOpen] = useState(false);
  const [pendingResultForCreateRo, setPendingResultForCreateRo] = useState<SearchResult | null>(null);
  // 使用者在 SelectorModal 選擇後的旗標：
  //   - 非 null 的 receipt id：resume — init_draft 已在 handleConfirmReceiptSelection
  //     就 eagerly 打過（resume 是 find 不會 create，沒有 orphan 風險）。
  //   - null：fresh — init_draft 延遲到「第一筆刷碼」(handleSubmitBarcode) 才打。
  //     會作為 resume_receipt_id 傳給 initDraftForResult；null = create 新 receipt。
  const [pendingResumeReceiptId, setPendingResumeReceiptId] = useState<string | null>(null);
  // The pending receipt id we're committing items into. Set by init_draft inside
  // handleConfirmVerification (just-in-time, to avoid orphan :pending receipts);
  // cleared by reset and after successful verify. Drives the POST /:id/verify
  // call and is also passed to <VideoRecorder /> so finalize can bind chunks.
  const [activeReceiptId, setActiveReceiptId] = useState<string | null>(null);

  // Full-bleed layout
  useEffect(() => {
    const contentElement = document.querySelector('.operator-content');
    contentElement?.classList.add('operator-content--full-bleed');

    return () => {
      contentElement?.classList.remove('operator-content--full-bleed');
    };
  }, []);

  // 重置時取消錄影（autoStart 由 prop 控制開始）
  useEffect(() => {
    const hadResult = prevSearchResultRef.current;
    prevSearchResultRef.current = searchResult;

    if (searchResult && !hadResult) {
      // 錄影開始後取得 video element 給偵測器
      setTimeout(() => setVideoEl(videoRecorderRef.current?.videoElement || null), 300);
    } else if (!searchResult && hadResult) {
      videoRecorderRef.current?.cancelRecording();
      setVideoEl(null);
      // searchResult 被清空（例如 context.reset() 從 verify 成功 / 異常 modal 觸發）
      // → 同步把使用者選的 resume target 也清掉，下一次搜尋從乾淨狀態開始。
      setPendingResumeReceiptId(null);
    }
  }, [searchResult]);

  // ✅ 離頁保護：只在「有東西會被丟掉」時才警告。
  //   - hasScannedItems：前端記憶體裡已掃進品項，離開 = 資料消失
  //   - hasActiveUploadsForLeave：影片 chunks 還在 queue，離開 = 影片不完整
  // 純粹「搜了 RO 號但什麼都還沒掃」不警告 — 沒進 DB、沒佔記憶體、沒人會在意。
  // 同 QC：beforeunload（瀏覽器原生關頁提示）+ useBlocker（SPA 導航 Modal）+
  // unmount cleanup（釋放 MediaStream tracks）。
  const hasActiveUploadsForLeave = uploadQueue.stats.uploading > 0 || uploadQueue.stats.pending > 0;
  const hasScannedItems = items.some((i: VerificationItem) => (i.received_quantity ?? 0) > 0);
  const shouldWarnOnLeave = hasScannedItems || hasActiveUploadsForLeave;

  const navigationBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      shouldWarnOnLeave && currentLocation.pathname !== nextLocation.pathname
  );

  // State-driven 離頁確認 Modal — 取代原本的 Modal.confirm，為了支援第三顆「暫存後
  // 離開」按鈕（Modal.confirm 的 footer 客製成本比較高，state-driven 更直觀也好測）。
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [isSavingProgress, setIsSavingProgress] = useState(false);

  const blockerHandledRef = useRef(false);
  useEffect(() => {
    if (navigationBlocker.state !== 'blocked') {
      blockerHandledRef.current = false;
      setLeaveModalOpen(false);
      return;
    }
    if (blockerHandledRef.current) return;
    blockerHandledRef.current = true;
    setLeaveModalOpen(true);
  }, [navigationBlocker.state]);

  // 暫存按鈕只在「有掃進品項、receipt 也已建好」時才顯示 —
  // activeReceiptId === null 表示連 init_draft 都還沒打，沒東西可暫存；
  // hasScannedItems === false 表示 planned 列存在但都還是 received_quantity=0，
  // 等於沒掃任何東西，暫存了也是空的。
  const canSaveProgress = !!activeReceiptId && hasScannedItems;

  const handleStayInPage = useCallback(() => {
    setLeaveModalOpen(false);
    navigationBlocker.reset?.();
  }, [navigationBlocker]);

  const handleDiscardAndLeave = useCallback(() => {
    setLeaveModalOpen(false);
    videoRecorderRef.current?.cancelRecording();
    navigationBlocker.proceed?.();
  }, [navigationBlocker]);

  // Items 整型成 backend 的 nested-attributes payload。沿用 verify 與 saveProgress
  // 共通的 schema（只送有量的 row，後端 match_return_order_items! 自動配 ROI）。
  // 抽出成獨立 callback 是為了給 sendBeacon 那條路徑共用（避免在 unload handler
  // 內 await Promise，sendBeacon 必須同步 fire）。
  const buildProgressItems = useCallback(() => {
    return items
      .filter((item: VerificationItem) => (item.received_quantity ?? 0) > 0)
      .map((item: VerificationItem) => ({
        product_id: item.product_id,
        inventory_type_id: item.inventory_type_id,
        quantity: item.quantity,
        received_quantity: item.received_quantity,
        batch: item.batch,
        expiration_date: item.expiration_date,
        manufacturing_date: item.manufacturing_date,
        custom_attributes: item.custom_attributes,
        serial_numbers: item.serial_numbers,
        return_order_item_id: item.return_order_item_id || null,
      }));
  }, [items]);

  const handleSaveProgressAndLeave = useCallback(async () => {
    if (!canSaveProgress || !activeReceiptId) return;
    setIsSavingProgress(true);
    try {
      const itemsToSave = buildProgressItems();

      const data = await returnReceiptService.saveProgress(activeReceiptId, itemsToSave);
      if (!data.success) {
        const code = data.message;
        const msg = code
          ? (t(`common.error.${code}`, { ...(data.interpolations || {}), defaultValue: code }) as string)
          : (t('common.error.operation_failed') as string);
        message.error(msg);
        return;  // stay open so operator can retry / pick another action
      }
      message.success(t('operator.return_verification.leave_confirm.save_progress_success', {
        defaultValue: '已暫存進度，下次掃描同單號可接續驗收',
      }));
      // 影片到此打住但 chunks 不 finalize（receipt 仍是 :pending，session 未結案）；
      // 下次 resume 時影片會接著錄新的 session — 跨 session 不接續是已知 trade-off，
      // 比影片不完整 + items 遺失輕得多。
      videoRecorderRef.current?.cancelRecording();
      navigationBlocker.proceed?.();
    } catch (err) {
      console.error('[ReturnOrderVerification] saveProgress failed:', err);
      message.error(t('common.error.operation_failed') as string);
    } finally {
      setIsSavingProgress(false);
    }
  }, [canSaveProgress, activeReceiptId, buildProgressItems, navigationBlocker, t]);

  // Beacon-ready snapshot：beforeunload handler 在 unload 瞬間必須同步抓資料，
  // 不能 await。把每次 render 的最新狀態壓進 ref，handler 從 ref 讀。這樣
  // listener 註冊一次就好，不用在每次 scan 都重綁。
  const beaconStateRef = useRef({
    canSaveProgress: false,
    activeReceiptId: null as string | null,
    buildItems: () => [] as ReturnType<typeof buildProgressItems>,
    hasActiveUploads: false,
    uploadCount: 0,
  });
  beaconStateRef.current = {
    canSaveProgress,
    activeReceiptId,
    buildItems: buildProgressItems,
    hasActiveUploads: hasActiveUploadsForLeave,
    uploadCount: uploadQueue.stats.uploading + uploadQueue.stats.pending,
  };

  // sendBeacon 抽出共用：beforeunload / pagehide / visibilitychange 三條路徑共享。
  // 多條 trigger 是必要的：
  //   - beforeunload：能跳原生 2-選項對話框，但 Chrome 92+ 要求 sticky activation
  //     才會跳；部分情境（HID 掃描器、無 click 互動）會被靜默跳過。
  //   - pagehide：reload / tab close / 前進後退都會 fire，比 beforeunload 可靠，
  //     但不能跳對話框（只負責 fire-and-forget 儲存）。
  //   - visibilitychange (hidden)：tab 切換 / 鎖屏 / 背景化會 fire，作為最後防線。
  // 三條都同一個 idempotent save_progress endpoint，重複觸發只是覆寫 = 安全。
  const fireSaveProgressBeacon = () => {
    const snap = beaconStateRef.current;
    if (!snap.canSaveProgress || !snap.activeReceiptId) return false;
    if (typeof navigator.sendBeacon !== 'function') return false;
    const blob = new Blob(
      [JSON.stringify({
        return_receipt: { return_receipt_items_attributes: snap.buildItems() },
      })],
      { type: 'application/json' }
    );
    return navigator.sendBeacon(
      `/api/operator/v1/return_receipts/${snap.activeReceiptId}/save_progress`,
      blob
    );
  };

  useEffect(() => {
    if (!shouldWarnOnLeave) return undefined;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      fireSaveProgressBeacon();
      const snap = beaconStateRef.current;
      const promptMessage = snap.hasActiveUploads
        ? `現在離開會中斷目前的作業，還有 ${snap.uploadCount} 個項目尚未完成處理，可能會遺失。`
        : '現在離開會中斷目前的作業，已操作的資料可能無法正確儲存。';
      event.preventDefault();
      event.returnValue = promptMessage;
      return promptMessage;
    };

    // pagehide 是 navigation API 標準推薦的「頁面要被丟棄」事件，比 beforeunload
    // 更可靠（特別是 mobile / 被瀏覽器 freeze 的 tab）。不能跳對話框，純粹備份存檔。
    const handlePageHide = () => {
      fireSaveProgressBeacon();
    };

    // visibilitychange 是最後一道保險：使用者切 tab、最小化、鎖螢幕都會 fire。
    // 同樣不跳對話框，只 fire-and-forget save_progress。
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        fireSaveProgressBeacon();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [shouldWarnOnLeave]);

  // Unmount safety-net：跳過 useBlocker 直接 unmount 的情境（父層 re-render、
  // error boundary 等）。cancelRecording 對未錄影的呼叫是 no-op，重複安全。
  useEffect(() => () => {
    videoRecorderRef.current?.cancelRecording();
  }, []);

  // POST /return_receipts/init_draft. Returns { ok, receipt } so callers can
  // distinguish failure from success and set activeReceiptId. Both fresh draft
  // (no resumeReceiptId) and resume paths share this entry point.
  //
  // ⚠️ 延遲建單原則：
  //   - Fresh：只在「第一筆品項刷碼」時才打（handleSubmitBarcode 內），純瀏覽
  //     / 開錯單號不會落單；建好之後影片 chunks 能即時上傳，不必整段掃描期都
  //     buffer 在記憶體（避免長驗收 OOM / queue overflow）。
  //   - Resume：在 SelectorModal 確認時 eagerly 打（只讀不寫，零 orphan
  //     風險），為的是拿到含 items 的 detail payload 去 overlay 先前進度。
  //
  // Invariant: result MUST carry a return_order / existing_return_order by this
  // point. The fulfillment-no-RO case is handled upstream in proceedWithResult
  // by opening CreateReturnOrderModal, which splices in the new RO before
  // letting the flow reach here. The early-return below stays as a guard so a
  // stale-call regression surfaces as a clear toast rather than a 422.
  const initDraftForResult = useCallback(async (result: SearchResult | null, resumeReceiptId: string | null) => {
    const returnOrderId = result?.return_order?.id || result?.existing_return_order?.id;
    if (!returnOrderId) {
      message.error(t('operator.return_verification.init_draft_no_return_order', {
        defaultValue: '此搜尋結果沒有對應的退貨單，無法開始驗收',
      }));
      return { ok: false } as const;
    }
    try {
      const data = await returnReceiptService.initDraft({
        returnOrderId,
        resumeReceiptId: resumeReceiptId || null,
      });
      if (!data.success) {
        const errKey = data.message
          ? `common.error.${data.message}`
          : 'operator.return_verification.init_draft_failed';
        message.error(t(errKey, { defaultValue: '無法建立驗收單，請重試' }) as string);
        return { ok: false } as const;
      }
      return { ok: true, receipt: data.data } as const;
    } catch (err) {
      console.error('[ReturnOrderVerification] init_draft failed:', err);
      message.error(t('operator.return_verification.init_draft_failed', {
        defaultValue: '無法建立驗收單，請重試',
      }));
      return { ok: false } as const;
    }
  }, [t]);

  // Turn a search result into a verification session.
  //   - fulfillment WITHOUT return_order / existing_return_order
  //       → open CreateReturnOrderModal; operator confirms (or edits) the
  //         suggested RO name. We then splice the new RO into the result and
  //         re-enter proceedWithResult so the downstream branches handle it.
  //   - active_pending_receipts present → open SelectorModal so operator picks
  //     resume vs 新開; handleConfirmReceiptSelection takes it from there.
  //   - none pending → 直接 loadResult；init_draft 留到確認驗收時才打（避免
  //     orphan :pending receipt）。預設 pendingResumeReceiptId 為 null = 新開。
  const proceedWithResult = useCallback(async (result: SearchResult) => {
    const hasRo = !!(result?.return_order?.id || result?.existing_return_order?.id);
    if (!hasRo && result?.fulfillment?.id) {
      setPendingResultForCreateRo(result);
      setCreateRoModalOpen(true);
      return;
    }

    const pending = result?.active_pending_receipts || [];
    if (pending.length >= 1) {
      setPendingResultForReceipt(result);
      setReceiptSelectorOpen(true);
      return;
    }
    setPendingResumeReceiptId(null);
    loadResult(result);
  }, [loadResult]);

  // CreateReturnOrderModal: BE just created the RO from the fulfillment. Splice
  // it into the search result (both `return_order` and `existing_return_order`
  // — the latter matches the RO-search payload shape so downstream code that
  // reads either key works the same). active_pending_receipts is forced to []
  // because a brand-new RO has none; re-enter proceedWithResult to take the
  // "fresh load" branch.
  const handleCreateRoConfirmed = useCallback(
    (newRo: { id: string; name: string; status: string }) => {
      const result = pendingResultForCreateRo;
      setCreateRoModalOpen(false);
      setPendingResultForCreateRo(null);
      if (!result) return;
      const updated: SearchResult = {
        ...result,
        return_order: newRo,
        existing_return_order: newRo,
        active_pending_receipts: [],
      };
      proceedWithResult(updated);
    },
    [pendingResultForCreateRo, proceedWithResult],
  );

  const handleCancelCreateRo = useCallback(() => {
    setCreateRoModalOpen(false);
    setPendingResultForCreateRo(null);
    focusBarcodeInput();
  }, [focusBarcodeInput]);

  // Suggested RO name shown in the modal. Prefer order.name when available
  // (operator-readable, often shorter than fulfillment.name); fall back to
  // fulfillment.name / number. BE applies the same fallback when name is blank.
  // No timestamp suffix — rare collisions (fulfillment re-scanned after its
  // previous RO was canceled) surface as a uniqueness error and the operator
  // edits the name; keeping the typical case short is worth that trade.
  const suggestedRoName = useMemo(() => {
    const ff = pendingResultForCreateRo?.fulfillment;
    if (!ff) return '';
    const orders = (pendingResultForCreateRo as { orders?: Array<{ name?: string }> } | null)?.orders;
    const orderName = orders?.[0]?.name;
    const base = orderName || ff.name || ff.number || '';
    return base ? `${base}-R` : '';
  }, [pendingResultForCreateRo]);

  // Lazy receipt creation gate — 在任何「會寫 received_quantity」的入口開始前確保
  // activeReceiptId 已就緒（fresh 路徑延遲到此才打 init_draft，避免 orphan :pending
  // receipt）。回傳 false 表示沒建成（已 message.error 或缺 searchResult），呼叫端
  // 應中止。Resume 路徑已在 handleConfirmReceiptSelection eager 建好，這裡直接 true。
  const ensureDraft = useCallback(async (): Promise<boolean> => {
    if (activeReceiptId) return true;
    if (!searchResult) return false;
    const draft = await initDraftForResult(searchResult, pendingResumeReceiptId);
    if (!draft.ok) return false;
    setActiveReceiptId(draft.receipt.id);
    return true;
  }, [activeReceiptId, searchResult, pendingResumeReceiptId, initDraftForResult]);

  // 處理條碼掃描
  const handleSubmitBarcode = useCallback(async (rawValue: string | null | undefined) => {
    const code = String(rawValue || '').trim();
    if (!code) return;
    // 清掉上一次「確認驗收」失敗留下的 local error 橫幅；context error 由
    // search / scanProduct 自己在開頭清，這裡只負責 page-local 那條。
    setError(null);
    clearBarcodeInput();

    // Phase 1: 還沒載入文件 → 搜尋
    if (!searchResult) {
      const result = await search(code);
      if (!result) return;

      if (result.match_count === 1) {
        proceedWithResult(result.results[0]);
      } else {
        setPendingResults(result.results);
        setPendingMatchType(result.match_type);
        setResultSelectModalOpen(true);
      }
      return;
    }

    // Phase 2: 已載入 → 檢查特殊條碼
    const specialBarcode = getSpecialBarcode(code) as { key?: string } | null;
    if (specialBarcode?.key === 'REPORT_ANOMALY') {
      setAnomalyMode(true);
      soundEffects.playScan();
      message.info(t('operator.return_verification.anomaly_mode_on', {
        defaultValue: '異常模式：請掃描異常商品'
      }));
      return;
    }

    // Phase 2: 掃商品條碼
    // 追蹤最近掃描的商品（用於影片字幕）
    const matchedItem = items.find((item: VerificationItem) => productMatchesCode(item.product, code));
    if (matchedItem?.product) {
      const isAnomaly = anomalyMode;
      setSubtitle({
        text: isAnomaly
          ? `⚠ ${matchedItem.product.sku}  ${matchedItem.product.name}`
          : `${matchedItem.product.sku}  ${matchedItem.product.name}`,
        timestamp: Date.now(),
        // TS strict 下 `cond && {...}` 不能 spread（會被當 boolean | {}）。
        // 改成 ternary，runtime 行為等價。
        ...(isAnomaly ? { color: '#ffff50', bgColor: 'rgba(200, 30, 30, 0.75)' } : {}),
      });
    }

    // Lazy receipt creation：第一筆品項刷碼時才打 init_draft。比起在
    // handleConfirmVerification 才建單，這裡建讓影片 chunks 立刻有 entityId
    // 可以即時上傳（不必整段掃描期間都 buffer，避免長驗收的記憶體 / queue
    // overflow 風險）。ensureDraft 失敗（內部已 message.error）就不要 scanProduct。
    if (!(await ensureDraft())) return;

    await scanProduct(code);
  }, [searchResult, search, proceedWithResult, scanProduct, setAnomalyMode, anomalyMode, items, t,
      ensureDraft]);

  // 註冊條碼處理器到全域 BarcodeInput
  useBarcodeInput(handleSubmitBarcode, [handleSubmitBarcode]);

  // 不在清單中的商品（透過 API 搜尋到的 unplanned）也觸發字幕
  useEffect(() => {
    if (scannedItem?.unplanned && scannedItem?.product) {
      setSubtitle({
        text: `⚠ ${scannedItem.product.sku}  ${scannedItem.product.name}`,
        timestamp: Date.now(),
        color: '#ffff50',
        bgColor: 'rgba(200, 30, 30, 0.75)',
      });
    }
  }, [scannedItem]);

  // 從 ResultSelectModal 選擇結果
  const handleSelectResult = useCallback((result: SearchResult) => {
    setResultSelectModalOpen(false);
    setPendingResults([]);
    setPendingMatchType(null);
    proceedWithResult(result);
  }, [proceedWithResult]);

  // Modal callback: receiptId === null → operator chose 新開; otherwise the
  // pending receipt id to resume.
  //
  // 分流：
  //   - Resume（receiptId 非 null）：init_draft 只讀不寫（後端對 resume_receipt_id
  //     是 find，不是 create，見 return_receipts_controller#init_draft），不會
  //     產生 orphan。立刻呼叫拿到完整 detail payload（含 items），才能在
  //     loadResultWithReceipt 把先前 progress 蓋回畫面。
  //   - Fresh（receiptId === null）：純瀏覽 / 開錯單號不該建 receipt，所以這裡
  //     只記下「新開」這個選擇；真正的 init_draft 留到 handleSubmitBarcode 第一
  //     筆品項刷碼時才打。
  const handleConfirmReceiptSelection = useCallback(async (receiptId: string | null) => {
    const result = pendingResultForReceipt;
    setReceiptSelectorOpen(false);
    setPendingResultForReceipt(null);
    if (!result) return;

    setPendingResumeReceiptId(receiptId);
    if (receiptId) {
      const draft = await initDraftForResult(result, receiptId);
      if (!draft.ok) {
        focusBarcodeInput();
        return;
      }
      setActiveReceiptId(draft.receipt.id);
      loadResultWithReceipt(result, draft.receipt);
    } else {
      loadResult(result);
    }
  }, [pendingResultForReceipt, initDraftForResult, loadResult, loadResultWithReceipt, focusBarcodeInput]);

  const handleCancelReceiptSelection = useCallback(() => {
    setReceiptSelectorOpen(false);
    setPendingResultForReceipt(null);
    focusBarcodeInput();
  }, [focusBarcodeInput]);

  const handleCancelResultSelect = useCallback(() => {
    setResultSelectModalOpen(false);
    setPendingResults([]);
    setPendingMatchType(null);
    focusBarcodeInput();
  }, []);

  // 手動點擊品項驗收（無條碼商品）。鉛筆編輯也走這條，編輯後會寫 received_quantity，
  // 所以開 Drawer 前必須確保 receipt 已 init（與掃碼路徑同樣的 lazy gate），否則
  // 確認驗收時 activeReceiptId 仍為 null，會撞「驗收單尚未初始化」護欄。
  const handleItemClick = useCallback(async (item: VerificationItem) => {
    if (!(await ensureDraft())) return;
    setScannedItem(item);
  }, [ensureDraft, setScannedItem]);

  // 處理重置按鈕
  const handleReset = useCallback(() => {
    Modal.confirm({
      title: t('operator.return_verification.reset_confirm_title', { defaultValue: '確認重置' }),
      content: t('operator.return_verification.reset_confirm_message', {
        defaultValue: '確定要重置嗎？所有未保存的驗收資料將會清除。'
      }),
      okText: t('common.button.confirm', { defaultValue: '確認' }),
      cancelText: t('common.button.cancel', { defaultValue: '取消' }),
      okButtonProps: { danger: true },
      onOk: () => {
        videoRecorderRef.current?.cancelRecording();
        setSubtitle(null);
        setActiveReceiptId(null);
        setPendingResumeReceiptId(null);
        reset();
        focusBarcodeInput();
      },
      onCancel: () => focusBarcodeInput(),
    });
  }, [reset, t]);

  // 開啟確認驗收 Modal
  const handleOpenConfirmModal = useCallback(() => {
    if (!searchResult) return;

    // 檢查所有品項是否都有填入庫存類型
    const itemsWithoutInventoryType = items.filter(
      (item: VerificationItem) => !item.inventory_type_id
    );

    if (itemsWithoutInventoryType.length > 0) {
      const productNames = itemsWithoutInventoryType
        .map((item: VerificationItem) => item.product?.name || item.product?.sku || '未知商品')
        .join('、');

      message.error(
        t('operator.return_verification.missing_inventory_type', {
          defaultValue: '以下品項尚未選擇庫存類型：{{products}}',
          products: productNames
        }) as string
      );
      soundEffects.playBuzzerError();
      return;
    }

    setConfirmModalOpen(true);
  }, [searchResult, items, t]);

  // Confirm verification — 此時 activeReceiptId 必定已被 handleSubmitBarcode（第一筆
  // 刷碼時）或 handleConfirmReceiptSelection（resume 路徑）設好。如果到這還是 null，
  // 代表「沒掃任何品項就按確認」這個不合理的 state（前面的 itemsToCreate.length === 0
  // 防呆已經攔下；理論上走不到這個 throw），保留錯誤路徑當 invariant 護欄。
  const handleConfirmVerification = useCallback(async () => {
    setLoading(true);
    setConfirmModalOpen(false);

    try {
      const itemsToCreate = items
        .filter((item: VerificationItem) => (item.received_quantity ?? 0) > 0)
        .map((item: VerificationItem) => ({
          product_id: item.product_id,
          inventory_type_id: item.inventory_type_id,
          quantity: item.quantity,
          received_quantity: item.received_quantity,
          batch: item.batch,
          expiration_date: item.expiration_date,
          manufacturing_date: item.manufacturing_date,
          custom_attributes: item.custom_attributes,
          serial_numbers: item.serial_numbers,
          return_order_item_id: item.return_order_item_id || null,
        }));

      if (itemsToCreate.length === 0) {
        message.warning(t('operator.return_verification.no_items_verified', { defaultValue: '沒有已驗收的品項' }));
        setLoading(false);
        return;
      }

      const receiptId = activeReceiptId;
      if (!receiptId) {
        throw new Error(t('operator.return_verification.no_active_receipt', {
          defaultValue: '驗收單尚未初始化，請重新掃描品項',
        }) as string);
      }

      const data = await returnReceiptService.verify(receiptId, itemsToCreate);
      if (!data.success) {
        const code = data.message;
        throw new Error(
          code
            ? (t(`common.error.${code}`, { ...(data.interpolations || {}), defaultValue: code }) as string)
            : (t('common.error.operation_failed') as string)
        );
      }

      message.success(t('operator.return_verification.create_and_verify_success', { defaultValue: '退貨驗收完成' }));

      // 結案後停掉 chunked session 並 enqueue finalize（worker 會把同 sessionId 的 chunks
      // 合成 webm 後 attach 到這張 return_receipt 的 return_receipt_videos）。剛剛
      // init_draft 取得的 receiptId 跟驗收成功的那張就是同一筆（resume 路徑也是）。
      const finalizedReceiptId = data.data?.id || receiptId;
      if (videoRecorderRef.current?.isRecording && finalizedReceiptId) {
        try {
          await videoRecorderRef.current.stopAndFinalize(finalizedReceiptId);
        } catch (videoErr) {
          console.error('[ReturnVerification] Failed to stop recording:', videoErr);
        }
      } else if (videoRecorderRef.current?.isRecording) {
        videoRecorderRef.current.cancelRecording();
      }

      setSubtitle(null);
      setActiveReceiptId(null);
      setPendingResumeReceiptId(null);
      reset();
      focusBarcodeInput();

    } catch (err) {
      console.error('[ReturnOrderVerification] Failed to confirm verification:', err);
      // err.message 此時應已是已翻譯字串（上方 throw 處統一）；防呆 fallback 通用文案。
      setError((err as Error).message || (t('common.error.operation_failed') as string));
      soundEffects.playBuzzerError();
    } finally {
      setLoading(false);
    }
  }, [activeReceiptId, items, reset, uploadQueue, t, focusBarcodeInput]);

  // 取消確認驗收
  const handleCancelConfirmModal = useCallback(() => {
    setConfirmModalOpen(false);
    focusBarcodeInput();
  }, []);

  // 合併 context 與 local 的 loading/error
  const isLoading = loading || contextLoading;
  const displayError = error || contextError;
  const errorMessage = displayError;

  // 顯示名稱
  const documentName = searchResult
    ? (searchResult.fulfillment?.name || searchResult.fulfillment?.number || searchResult.return_order?.name || '')
    : '';

  return (
    <div className={`return-verification-page${anomalyMode ? ' return-verification-page--anomaly' : ''}`}>
      {/* 左側：退貨商品列表 */}
      <div className="return-verification-page__left">
        <header className="return-verification-page__left-header">
          {/* Header 區域 - 訂單資訊 + 狀態 */}
          <div className="return-verification-page__header-blocks">
            <div className="return-verification-page__header-block return-verification-page__order-status-block">
              {searchResult ? (
                <>
                  {/* 訂單標籤 */}
                  <div className="return-verification-page__order-info">
                    <span className="return-verification-page__order-label">
                      {t('operator.return_verification.document_label', { defaultValue: '驗收單' })}:
                    </span>
                    <span className="return-verification-page__order-name">
                      {documentName}
                    </span>
                  </div>
                  {/* 狀態指示器 */}
                  <div className="return-verification-page__status-indicator">
                    <span className="return-verification-page__status-text">
                      {t('operator.return_verification.state.scanning_items', { defaultValue: '掃描退貨商品' })}
                    </span>
                  </div>
                  {/* 重置按鈕 */}
                  <Button
                    size="small"
                    danger
                    onClick={handleReset}
                    className="return-verification-page__reset-button"
                    style={{ marginRight: '8px' }}
                  >
                    {t('common.button.reset', { defaultValue: '重置' })}
                  </Button>
                  {/* 回報異常按鈕 */}
                  <Button
                    size="small"
                    type={anomalyMode ? 'primary' : 'default'}
                    danger={anomalyMode}
                    icon={<WarningOutlined />}
                    onClick={() => {
                      setAnomalyMode(!anomalyMode);
                      if (!anomalyMode) {
                        message.info(t('operator.return_verification.anomaly_mode_on', {
                          defaultValue: '異常模式：請掃描異常商品'
                        }));
                      }
                    }}
                    style={{ marginRight: '8px' }}
                  >
                    {t('operator.return_verification.report_anomaly', { defaultValue: '回報異常' })}
                  </Button>
                  {/* 確認驗收按鈕 */}
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={handleOpenConfirmModal}
                    className="return-verification-page__confirm-button"
                  >
                    {t('common.button.confirm', { defaultValue: '確認驗收' })}
                  </Button>
                </>
              ) : (
                <>
                  {/* 未載入訂單時的狀態 */}
                  <div className="return-verification-page__status-indicator return-verification-page__status-indicator--waiting">
                    <span className="return-verification-page__status-text">
                      {t('operator.return_verification.state.awaiting_scan', { defaultValue: '等待掃描單號' })}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <div className="return-verification-page__left-content">
          {!isLoading && errorMessage && (
            <Alert
              type="error"
              message={errorMessage}
              showIcon
              className="return-verification-page__alert"
            />
          )}
          {anomalyMode && (
            <Alert
              type="error"
              icon={<WarningOutlined />}
              message={t('operator.return_verification.anomaly_mode_banner', {
                defaultValue: '異常模式已啟動 — 下一件掃描的商品將開啟異常處理視窗'
              })}
              banner
              showIcon
              style={{ marginBottom: 8, fontSize: 16, fontWeight: 600 }}
            />
          )}
          {isLoading ? (
            <div className="return-verification-page__spinner">
              <Spin size="large" />
            </div>
          ) : searchResult && items.length > 0 ? (
            <div className="return-verification-page__items-table">
              <ReturnReceiptItemsTable
                items={items}
                highlightedItemId={highlightedItemId}
                onItemClick={handleItemClick}
              />
            </div>
          ) : (
            <div className="return-verification-page__placeholder">
              <Empty description={t('operator.return_verification.no_orders', { defaultValue: '請掃描退貨單開始驗收' })} />
            </div>
          )}
        </div>
      </div>


      {/* 右側：條碼輸入 + 錄影區域 */}
      <div className="return-verification-page__right">
        <div className="return-verification-page__right-header">
          <GlobalBarcodeInput
            placeholder={t('operator.return_verification.barcode_input_placeholder', {
              defaultValue: '請掃描或輸入條碼',
            })}
            barcodeShortcuts={searchResult ? ['REPORT_ANOMALY'] : []}
          />

        </div>
        <div className="return-verification-page__right-content">
          <div className="return-verification-page__video-recorder" style={{ position: 'relative' }}>
            {/*
              影片生命週期：autoStart={!!searchResult} → 一搜到結果就開始錄；
              activeReceiptId 在「第一筆品項刷碼」前是 null（fresh 路徑）或在
              SelectorModal 選 resume 時就先設好（resume 路徑）。

              Buffer 是「掃第一筆前那 0-2 秒」的 fallback — chunks 在沒有 receiptId
              時暫存在 VideoRecorder 內部 ref，id 一進來 useEffect 立刻 drain。
              正常情境下 buffer 不會累積超過個位數 chunks。
            */}
            <VideoRecorder
              ref={videoRecorderRef}
              orderNumber={documentName}
              subtitle={subtitle}
              magnifier={magnifier}
              autoStart={!!searchResult}
              placeholderText={t('operator.return_verification.video_placeholder', { defaultValue: '掃描單號後自動開始錄影' })}
              returnReceiptId={activeReceiptId}
              uploadQueue={uploadQueue}
            />
            {/* __DEBUG__: overlay 對齊影片預覽區（bottom:0 + 16:10 比例匹配 canvas） */}
            {__DEBUG__ && autoZoomEnabled && searchResult && (
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                aspectRatio: '16 / 10',
                pointerEvents: 'none',
              }}>
                {/* 搜尋區域（中央 65%）— 黃色虛線 */}
                <div style={{
                  position: 'absolute',
                  left: '17.5%', top: '17.5%',
                  width: '65%', height: '65%',
                  border: '1px dashed rgba(255,255,0,0.3)',
                  boxSizing: 'border-box',
                }} />
                {/* blob 邊界 — 通過=綠色, 未通過=紅色 */}
                {detectorDebug?.debugRegion && (() => {
                  const r = detectorDebug.debugRegion;
                  const color = detectorDebug.detected ? '#00ff00' : '#ff4444';
                  return (
                    <>
                      <div style={{
                        position: 'absolute',
                        left: `${(r.x - r.w / 2) * 100}%`,
                        top: `${(r.y - r.h / 2) * 100}%`,
                        width: `${r.w * 100}%`,
                        height: `${r.h * 100}%`,
                        border: `2px solid ${color}`,
                        borderRadius: 2,
                        boxSizing: 'border-box',
                      }} />
                      <div style={{
                        position: 'absolute',
                        left: `${r.x * 100}%`,
                        top: `${r.y * 100}%`,
                        width: 8, height: 8,
                        background: color,
                        borderRadius: '50%',
                        transform: 'translate(-50%, -50%)',
                      }} />
                    </>
                  );
                })()}
              </div>
            )}
            <VideoUploadQueueStatus uploadQueue={uploadQueue} />
          </div>
          <Collapse
            size="small"
            ghost
            items={[{
              key: 'settings',
              label: <span style={{ fontSize: 12, color: '#8c8c8c' }}><SettingOutlined /> {t('operator.return_verification.video_settings', { defaultValue: '錄影設定' })}</span>,
              children: (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 13 }}>
                      {t('operator.return_verification.auto_zoom_document', { defaultValue: '放大單據' })}
                    </span>
                    <Switch
                      size="small"
                      checked={autoZoomEnabled}
                      onChange={setAutoZoomEnabled}
                    />
                  </div>
                  {__DEBUG__ && autoZoomEnabled && detectorDebug && (
                    <div style={{
                      fontSize: 11,
                      fontFamily: 'monospace',
                      padding: '6px 8px',
                      borderRadius: 4,
                      background: detectorDebug.detected ? '#f6ffed' : '#fff2f0',
                      border: `1px solid ${detectorDebug.detected ? '#b7eb8f' : '#ffccc7'}`,
                      lineHeight: 1.6,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong>{detectorDebug.detected
                          ? `✓ 偵測到單據`
                          : detectorDebug.reason === 'no_light_region'
                            ? '✗ 淺色區域不足'
                            : detectorDebug.scores && parseInt(detectorDebug.scores.content) < 8
                              ? '✗ 無文字內容'
                              : detectorDebug.scores && parseInt(detectorDebug.scores.shape) < 35
                                ? '✗ 非矩形'
                                : `✗ 分數不足`
                        }</strong>
                        <span style={{
                          fontSize: 10,
                          padding: '1px 6px',
                          borderRadius: 3,
                          background: detectorDebug.status === 'cooldown' ? '#ffe58f' : detectorDebug.status === 'locked' ? '#b7eb8f' : '#d9d9d9',
                          color: '#333',
                        }}>
                          {({ detecting: '偵測中', locked: '鎖定', cooldown: `冷卻 ${Math.ceil((detectorDebug.cooldownMs || 0) / 100) / 10}s` } as Record<string, string>)[detectorDebug.status] || detectorDebug.status}
                        </span>
                      </div>
                      {detectorDebug.scores && (() => {
                        const s = detectorDebug.scores;
                        const d = detectorDebug;
                        const bar = (score: number | string, max: number) => {
                          const pct = max > 0 ? parseInt(String(score)) / max * 100 : 0;
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ flex: 1, height: 6, background: '#f0f0f0', borderRadius: 3 }}>
                                <div style={{ width: `${pct}%`, height: '100%', background: pct >= 60 ? '#52c41a' : pct >= 30 ? '#faad14' : '#ff4d4f', borderRadius: 3 }} />
                              </div>
                              <span style={{ minWidth: 36, textAlign: 'right' }}>{score}</span>
                            </div>
                          );
                        };
                        const groupStyle = { fontSize: 10, fontWeight: 600, color: '#666', marginTop: 4 };
                        const passIcon = (pass: boolean) => pass ? ' ✅' : ' ❌';
                        return (
                          <>
                            <div style={groupStyle}>A. 淺色矩形 ({s.shape}, 及格 35){passIcon(parseInt(s.shape) >= 35)}</div>
                            <div>　淺色像素 ({d.lightPixels}){bar(s.light, 10)}</div>
                            <div>　區塊面積 ({d.blobArea}){bar(s.area, 15)}</div>
                            <div>　填充率 ({d.fillRatio}){bar(s.fill, 20)}</div>
                            <div>　邊緣直線 ({d.edgeBest3Avg}){bar(s.edge, 30)}</div>
                            {d.edgeDetail && <div style={{ color: '#999', paddingLeft: 12 }}>{d.edgeDetail}</div>}
                            <div style={groupStyle}>B. 文字內容 ({s.content}, 及格 8){passIcon(parseInt(s.content) >= 8)}</div>
                            <div>　內容密度 ({d.darkRatio}){bar(s.dark, 25)}</div>
                            <div style={{ borderTop: '1px solid #d9d9d9', marginTop: 4, paddingTop: 4, fontWeight: 600 }}>
                              總分: {s.total}
                              {detectorDebug.detected ? ' 🟢' : ' 🔴'}
                            </div>
                          </>
                        );
                      })()}
                      {!detectorDebug.scores && (
                        <div>淺色像素: {detectorDebug.lightPixels}, 區塊: {detectorDebug.blobArea}</div>
                      )}
                      {detectedRegion && (
                        <div style={{ borderTop: '1px solid #d9d9d9', marginTop: 4, paddingTop: 4 }}>
                          <div>區域: {(detectedRegion.w * 100).toFixed(1)}% x {(detectedRegion.h * 100).toFixed(1)}%</div>
                          <div>中心: ({(detectedRegion.x * 100).toFixed(1)}%, {(detectedRegion.y * 100).toFixed(1)}%)</div>
                          <div>放大: {magnifier ? `${magnifier.zoom.toFixed(1)}x` : '跳過 (區域已夠大)'}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ),
            }]}
          />
        </div>
      </div>

      {/* 搜尋結果選擇 Modal */}
      <ResultSelectModal
        open={resultSelectModalOpen}
        results={pendingResults}
        matchType={pendingMatchType}
        onSelect={handleSelectResult}
        onCancel={handleCancelResultSelect}
      />

      {/* 接續/新開驗收單選擇 Modal */}
      <ReturnReceiptSelectorModal
        open={receiptSelectorOpen}
        activePendingReceipts={(pendingResultForReceipt?.active_pending_receipts || []) as any}
        onConfirm={handleConfirmReceiptSelection}
        onCancel={handleCancelReceiptSelection}
      />

      {/* 建立 RO Modal（掃到 fulfillment 但還沒有對應 RO 時跳出） */}
      <CreateReturnOrderModal
        open={createRoModalOpen}
        fulfillment={pendingResultForCreateRo?.fulfillment || null}
        items={(pendingResultForCreateRo?.items || []) as any}
        suggestedName={suggestedRoName}
        onCreated={handleCreateRoConfirmed}
        onCancel={handleCancelCreateRo}
      />

      {/* 驗收確認 Drawer（掃描商品後的側拉抽屜） */}
      <ReturnVerificationDrawer />

      {/* 確認驗收 Modal */}
      <ConfirmVerificationModal
        open={confirmModalOpen}
        returnOrder={searchResult ? {
          name: documentName,
          items: items,
          _isFromFulfillment: !!searchResult.fulfillment,
        } : null}
        onConfirm={handleConfirmVerification}
        onCancel={handleCancelConfirmModal}
      />

      {/* 離頁確認 Modal — 由 useBlocker 觸發；視 canSaveProgress 顯示 2 或 3 顆按鈕 */}
      <Modal
        open={leaveModalOpen}
        title={t('operator.return_verification.leave_confirm.title', { defaultValue: '確定要離開嗎？' })}
        onCancel={handleStayInPage}
        closable={!isSavingProgress}
        maskClosable={!isSavingProgress}
        footer={[
          <Button key="stay" onClick={handleStayInPage} disabled={isSavingProgress}>
            {t('common.button.stay', { defaultValue: '留下' })}
          </Button>,
          canSaveProgress && (
            <Button
              key="save"
              type="primary"
              loading={isSavingProgress}
              onClick={handleSaveProgressAndLeave}
            >
              {t('operator.return_verification.leave_confirm.save_progress', { defaultValue: '暫存後離開' })}
            </Button>
          ),
          <Button key="discard" danger onClick={handleDiscardAndLeave} disabled={isSavingProgress}>
            {t(canSaveProgress
              ? 'operator.return_verification.leave_confirm.discard'
              : 'common.button.leave',
              { defaultValue: canSaveProgress ? '放棄並離開' : '離開' })}
          </Button>,
        ].filter(Boolean)}
      >
        {hasActiveUploadsForLeave
          ? t('operator.return_verification.leave_confirm.uploads', {
              count: uploadQueue.stats.uploading + uploadQueue.stats.pending,
              defaultValue: `現在離開會中斷目前的作業，還有 ${uploadQueue.stats.uploading + uploadQueue.stats.pending} 個項目尚未完成處理，可能會遺失。`,
            })
          : canSaveProgress
            ? t('operator.return_verification.leave_confirm.session_with_draft', {
                count: items.filter((i: VerificationItem) => (i.received_quantity ?? 0) > 0).length,
                defaultValue: '目前掃描的進度尚未送出。選擇「暫存後離開」會把已掃的品項存起來，下次掃描同單號可接續驗收；選擇「放棄並離開」會把掃描資料丟棄。',
              })
            : t('operator.return_verification.leave_confirm.session', {
                defaultValue: '現在離開會中斷目前的作業，已操作的資料可能無法正確儲存。',
              })}
      </Modal>
    </div>
  );
}

export default function ReturnOrderVerificationIndex() {
  return (
    <BarcodeInputProvider>
      <ReturnReceiptAcceptanceProvider>
        <ReturnOrderVerificationContent />
      </ReturnReceiptAcceptanceProvider>
    </BarcodeInputProvider>
  );
}
